import * as React from "react"
import compose from "recompose/compose"
import getContext from "recompose/getContext"
import lifecycle from "recompose/lifecycle"
import withContext from "recompose/withContext"
import withHandlers from "recompose/withHandlers"
import withState from "recompose/withState"
import PropTypes from "prop-types"
import ttt from "@xingternal/ticktricktrack"
import { withCurrentUser } from "../withCurrentUser"
import { withPermissions } from "../withPermissions"
import { isAdmin } from "@/helpers/authorization"

export const CHANNEL = "wbxri/RecruiterInsights"

let trackingInstance

const initTracking = () => {
  if (trackingInstance === undefined) {
    trackingInstance = ttt.init(window.environment)
  }

  return trackingInstance
}

const getMemberships = (permissions) => {
  const has360 = permissions.clients.some((client) => client.is_360)

  const membershipOptions = {
    360: has360,
    ...permissions.products,
    "xtm+": permissions.products.xtm && permissions.products.xtp,
  }

  const activeMemberships = Object.keys(membershipOptions).filter(
    (option) => membershipOptions[option],
  )

  return {
    PropContextDimension1: `xri_${activeMemberships.join("_")}`,
    PropContextDimension3: `xri_${isAdmin(permissions) ? "admin" : "user"}`,
  }
}

const getSubscriptions = (
  propMemberships,
  propContextDimension1,
  propContextDimension3,
) => {
  const cleanedPropContextDimension1 = propContextDimension1
    ? propContextDimension1.replace(/^xri_/, "")
    : ""
  const user = propContextDimension3 === "xri_admin" ? "xri_1" : "xri_0"
  return `${propMemberships},${cleanedPropContextDimension1},${user}`
}

const TrackingProvider = compose(
  withCurrentUser,
  withPermissions,
  withState("baseData", "setBaseData", {}),
  withState("contextData", "setContextData", {}),
  withHandlers({
    setUserData:
      ({ currentUser, permissions, setBaseData, setContextData }) =>
      () => {
        setBaseData(currentUser.trackingData)
        setContextData(getMemberships(permissions))
      },
    trackAction:
      ({ contextData, isImpersonated }) =>
      (action, data, eventName = "PropTrackAction") => {
        if (isImpersonated) return

        initTracking()
          .then(() =>
            ttt.event(eventName, {
              [eventName]: action,
              ...contextData,
              ...data,
            }),
          )
          .catch((error) => console.error(error))
      },
    trackPage:
      ({ baseData, contextData, isImpersonated }) =>
      (page, data) => {
        if (isImpersonated) return
        initTracking()
          .then(() =>
            ttt.pageview(CHANNEL, `${CHANNEL}/${page}`, baseData, {
              ...contextData,
              data,
            }),
          )
          .catch(() => null)
      },
    nwTrackPage:
      ({ currentUser, baseData, contextData, isImpersonated }) =>
      (page, data) => {
        if (isImpersonated) return

        const propMemberships = baseData.PropMemberships
        const propContextDimension1 = contextData.PropContextDimension1
        const propContextDimension3 = contextData.PropContextDimension3
        const subscriptions = getSubscriptions(
          propMemberships,
          propContextDimension1,
          propContextDimension3,
        )

        const defaultProps = {
          application: "wbxri",
          channel: "web",
          schema_version: "1.20.0",
          user_id: currentUser?.id ? currentUser.id.toString() : "",
          event_schema: "basic",
          sent_by: "recruiter_insights",
          page: `recruiterinsights/${page}`,
          subscriptions,
          event: "viewed_screen",
        }
        initTracking()
          .then(() => ttt.nwtEvent({ ...defaultProps, ...data }))
          .catch(() => null)
      },
  }),
  lifecycle({
    componentDidMount() {
      const { currentUser, setUserData, isImpersonated } = this.props

      !isImpersonated && initTracking()

      setUserData(currentUser)
    },
  }),
  withContext(
    {
      trackAction: PropTypes.func,
      trackPage: PropTypes.func,
      nwTrackPage: PropTypes.func,
    },
    ({ trackAction, trackPage, nwTrackPage }) => ({
      trackAction,
      trackPage,
      nwTrackPage,
    }),
  ),
)((props) => React.Children.only(props.children))

export const withTracking = (pagename) =>
  compose(
    getContext({
      trackAction: PropTypes.func,
      trackPage: PropTypes.func,
    }),
    lifecycle({
      componentDidMount() {
        pagename && this.props.trackPage(pagename)
      },
    }),
  )

export const withNWTracking = (pagename) =>
  compose(
    getContext({
      nwTrackPage: PropTypes.func,
    }),
    lifecycle({
      componentDidMount() {
        pagename && this.props.nwTrackPage(pagename)
      },
    }),
  )

export default TrackingProvider
